import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as yup from 'yup';

import CheckboxGroup from '../CheckboxGroup';
import Checkbox from '../Checkbox';
import Message from '../Message';
import Text from '../Text';
import Input from '../Input';
import Select, { CountrySelector } from '../Select';
import Datepicker from '../Datepicker';
import Button from '../Button';
import { View, Col, Row } from '../View';
import Section from '../Section';

import { styleUtils } from '../../utils';

import styles from './styles';
import TextArea from '../TextArea';

const { style } = styleUtils;

const COMPONENTS = {
  input: Input,
  checkbox: Checkbox,
  checkboxGroup: CheckboxGroup,
  sectionTitle: Text,
  select: Select,
  countrySelector: CountrySelector,
  datepicker: Datepicker,
  textarea: TextArea
};

const Container = style(View, styles.container);
const TopButtonContainer = style(View, styles.topButtonContainer);
const TopContainer = style(View, styles.topContainer);
const FormSection = style(View, styles.formSection);
const TitleContainer = style(View, styles.titleContainer);
const Title = style(Text);
const MandatoryFieldsLabelContainer = style(
  View,
  styles.mandatoryFieldsLabelContainer
);
const MandatoryFieldsLabel = style(Text, styles.mandatoryFieldsLabel);
const MessageContainer = style(View, styles.messageContainer);
const CollapseFormContainer = style(View, styles.collapseFormContainer);
const CollapseFormLabel = style(Text, styles.collapseFormLabel);
const ExpandLabelContainer = style(View, styles.collapseFormContainer);
const ExpandLabel = style(Text, styles.collapseFormLabel);
const BottomComponentContainer = style(View, styles.bottomComponentContainer);
const ButtonsContainer = style(View, styles.buttonsContainer);
const SectionTitleContainer = style(View, styles.sectionTitleContainer);

class Form extends Component {
  constructor(props) {
    super(props);
    const { showOnlyButtons } = props;
    this.state = {
      collapsed: false
    };
    if (!showOnlyButtons) this.initializeValues();
  }

  initializeValues = shouldSetValues => {
    const { formSchema, collapsable, showMandatoryFieldsLabel } = this.props;
    this.properties = {};
    this.initialValues = {};
    this.showMandatoryFieldsLabel = showMandatoryFieldsLabel;
    this.showCollapseLabel = collapsable;
    formSchema.forEach(rowData =>
      rowData.forEach(({ columns }) => {
        columns.forEach(({ name, value, checked, required }) => {
          // When schemas change, we need to setFieldValues of all the fields of the new schema
          const _value = checked || value;
          if (shouldSetValues && this.setFieldValue) {
            this.setFieldValue(name, _value);
          }
          this.initialValues[name] = _value;
        });
      })
    );
  };

  handleOnSubmit = async (values, { setSubmitting }) => {
    const { nextButton } = this.props;
    nextButton.action();
    setSubmitting(false);
  };

  multiFormSubmit = async () => {
    const {
      multiForm = {
        1: {
          validateForm: this.validateForm,
          setFieldTouched: this.setFieldTouched,
          setCollapsed: this.setCollapsed
        }
      },
      clearMultiForm,
      nextButton,
      submitMultiForm
    } = this.props;

    let areFormsValid = true;
    let multiFormData = Object.keys(multiForm);

    if(multiFormData.includes("contactInformation")) {
      multiFormData = ["contactInformation"];
    }

    for (let i = 0; i < multiFormData.length; i++) {
      const { validateForm, setFieldTouched, setCollapsed } = multiForm[
        multiFormData[i]
      ];
      if(typeof validateForm === 'function') {
        const errors = await validateForm();
        const errorFields = Object.keys(errors);
        errorFields.forEach(field => setFieldTouched(field, true));
        if (errorFields.length > 0) {
          areFormsValid = false;
          setCollapsed(false);
        }
      }
    }

    if (areFormsValid) {
      submitMultiForm(multiFormData);
      clearMultiForm();
      nextButton.action();
    }
  };

  handleGoBack = () => {
    const { previousButton, clearMultiForm } = this.props;
    clearMultiForm();
    previousButton.action();
  };

  processErrorMessages = (errors, touched) => {
    const messages = [];
    if (!Object.keys(touched).length) {
      this.showMessages = messages;
      this.showMandatoryFields = this.showMandatoryFieldsLabel;
      this.showCollapseLabel = true;
      return;
    }

    Object.keys(errors).forEach(fieldKey => {
      if (touched[fieldKey]) messages.push(errors[fieldKey]);
    });
    this.showMessages = messages;
    this.showMandatoryFields =
      !messages.length && this.showMandatoryFieldsLabel;
    this.showCollapseLabel = !messages.length;
  };

  onFieldChange = (name, type, values) => (value, e) => {
    const { onChange } = this.props;
    let val = value;
    switch (type) {
      case 'input': {
        const { target } = value;
        val = target.value;
        break;
      }
      case 'textarea': {
        const { target } = value;
        val = target.value;
        break;
      }
      case 'checkbox': {
        val = e;
        break;
      }
      case 'checkboxGroup': {
        this.setFieldTouched(name);
        if (e) val = { ...values[name], [value]: e };
        else {
          val = { ...values[name] };
          delete val[value];
          if (Object.keys(val).length === 0) val = undefined;
        }
        break;
      }
      default:
        break;
    }

    onChange && onChange(name, val);
    this.setFieldValue(name, val);
  };

  customHandleBlur = name => () => this.setFieldTouched(name);

  handleCollapseForm = () => this.setState({ collapsed: true });

  handleDatepickerTouched = name => isOpen => {
    if (!isOpen) this.setFieldTouched(name);
  };

  handleCheckboxGroupChange = (name, checked) => {
    if (checked) this.setFieldTouched(name);
  };

  renderComponent = ({
    component,
    touched,
    errors,
    name,
    index,
    values,
    label,
    handleBlur,
    options,
    defaultValue,
    styles,
    sublabel,
    ...restOfProps
  }) => {
    const Component = COMPONENTS[component];

    const error =
      errors[name] && touched[name]
        ? {
            error: errors[name] && touched[name],
            message: errors[name] && touched[name] ? errors[name] : undefined
          }
        : {};

    const commonOpts = {
      key: index,
      tabIndex: 0,
      label,
      name,
      onChange: this.onFieldChange(name, component, values),
      value: values[name]
    };

    const preventSelectDirtValue = ( value, opts = [{}]) => {
      if(!opts.some(item => item.value === value)) {
        return null
      }
      return value
    };

    switch (component) {
      case 'sectionTitle': {
        const flexStyles = sublabel
          ? { display: 'flex', flexDirection: 'column' }
          : {};
        return (
          <SectionTitleContainer
            key={index}
            style={{ ...flexStyles, ...styles }}
          >
            <Component size="large">{values[name]}</Component>
            {sublabel && <Text size="small">{sublabel}</Text>}
          </SectionTitleContainer>
        );
      }
      case 'input': {
        return (
          <Component
            fluid
            sublabel={sublabel}
            onBlur={handleBlur}
            {...commonOpts}
            {...error}
            {...restOfProps}
          />
        );
      }
      case 'checkbox': {
        return (
          <Component
            checked={values[name]}
            {...commonOpts}
            {...error}
            {...restOfProps}
          >
            {label}
          </Component>
        );
      }
      case 'checkboxGroup': {
        return <Component {...commonOpts} {...error} {...restOfProps} />;
      }
      case 'textarea': {
        return (
          <Component
            sublabel={sublabel}
            onBlur={handleBlur}
            {...commonOpts}
            {...error}
            {...restOfProps}
          />
        );
      }
      case 'select': {
        const { preventDirt } = restOfProps || {};
        const cleanValue = preventDirt ? {value: preventSelectDirtValue(commonOpts.value, options)} : null;

        return (
          <Component
            options={options}
            defaultValue={defaultValue}
            onBlur={this.customHandleBlur(name)}
            {...commonOpts}
            {...error}
            {...restOfProps}
            {...cleanValue}
          />
        );
      }
      case 'countrySelector': {
        return (
          <Component
            defaultValue={defaultValue}
            onBlur={this.customHandleBlur(name)}
            {...commonOpts}
            {...error}
            {...restOfProps}
          />
        );
      }
      case 'datepicker': {
        return (
          <Component
            onOpenChange={this.handleDatepickerTouched(name)}
            {...commonOpts}
            {...error}
            {...restOfProps}
          />
        );
      }
      default:
        return null;
    }
  };

  processMultiForm = (
    setFieldTouched,
    setFieldValue,
    setErrors,
    validateForm
  ) => {
    const { addToMultiForm, formId } = this.props;
    if (formId && !this.isMultiFormSet) {
      addToMultiForm(formId, {
        setFieldTouched,
        setFieldValue,
        setErrors,
        validateForm,
        setCollapsed: this.setCollapsed
      });
      this.isMultiFormSet = true;
    }
  };

  renderForm = () => ({
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    isValid,
    setFieldTouched,
    setFieldValue,
    setErrors,
    validateForm
  }) => {
    const {
      t,
      formSchema,
      title,
      previousButton,
      nextButton,
      topButton,
      showFormButtons = true,
      collapsable = false,
      description,
      bottomComponent,
      isMultiForm,
      showMessages
    } = this.props;

    const { collapsed } = this.state;
    this.handleChange = handleChange;
    this.setFieldValue = setFieldValue;
    this.setFieldTouched = setFieldTouched;
    this.setErrors = setErrors;
    this.validateForm = validateForm;
    this.processErrorMessages(errors, touched);
    this.processMultiForm(
      setFieldTouched,
      setFieldValue,
      setErrors,
      validateForm
    );
    const onSubmit = isMultiForm ? {} : { onSubmit: handleSubmit };
    const form = (
      <>
        <form {...onSubmit}>
          {collapsed ? (
            <FormCollapsed
              t={t}
              title={title}
              description={description}
              topButton={topButton}
              onExpandClick={() => this.setState({ collapsed: false })}
            />
          ) : (
            <Section className="section">
              <FormSection className="section">
                <TopContainer className="container">
                  {title && <FormTitle>{title}</FormTitle>}
                  {topButton && <TopButton {...topButton} />}
                </TopContainer>
                {formSchema.map((rowData, formIndex) => (
                  <Row key={formIndex} gutter={20}>
                    {rowData.map(({ columns, span }, rowIndex) => (
                      <Col span={span} key={rowIndex}>
                        {columns &&
                          columns.map(
                            (
                              {
                                component,
                                name,
                                label,
                                options,
                                defaultValue,
                                show,
                                style,
                                ...restOfProps
                              },
                              index
                            ) => {
                              if (!show) return null;
                              return (
                                <div style={style} key={`${component}_${name}`}>
                                  {this.renderComponent({
                                    component,
                                    touched,
                                    errors,
                                    name,
                                    index,
                                    values,
                                    label,
                                    handleBlur,
                                    options,
                                    defaultValue,
                                    ...restOfProps
                                  })}
                                </div>
                              );
                            }
                          )}
                      </Col>
                    ))}
                  </Row>
                ))}
                <MandatoryFields t={t} show={this.showMandatoryFields} />
              </FormSection>
              <CollapseFormButton
                t={t}
                action={this.handleCollapseForm}
                show={this.showCollapseLabel && collapsable}
              />
              {showMessages && <ErrorMessages messages={this.showMessages} />}
            </Section>
          )}
          <BottomComponent component={bottomComponent} />
        </form>
        <FormButtons
          handleGoBack={this.handleGoBack}
          multiFormSubmit={this.multiFormSubmit}
          isMultiForm={isMultiForm}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          previousButton={previousButton}
          nextButton={nextButton}
          show={showFormButtons}
        />
      </>
    );
    return form;
  };

  setCollapsed = collapsed => this.setState({ collapsed });

  render() {
    const {
      isMultiForm,
      validationSchema,
      showOnlyButtons,
      nextButton,
      previousButton,
      fluid,
      updateInitialValues
    } = this.props;
    const onSubmit = isMultiForm ? {} : { onSubmit: this.handleOnSubmit };
    if (updateInitialValues) this.initializeValues(true);
    return (
      <Container fluid={fluid}>
        {showOnlyButtons ? (
          <FormButtons
            handleGoBack={this.handleGoBack}
            showOnlyButtons
            previousButton={previousButton}
            nextButton={nextButton}
            show
          />
        ) : (
          <Formik
            initialValues={this.initialValues}
            validationSchema={validationSchema}
            {...onSubmit}
            render={this.renderForm()}
          />
        )}
      </Container>
    );
  }
}

const BottomComponent = ({ component }) => (
  <BottomComponentContainer>{component}</BottomComponentContainer>
);

const FormCollapsed = ({ t, title, description, topButton, onExpandClick }) => (
  <Section>
    <FormSection>
      <TopContainer>
        <TitleContainer>
          <Title size="xlarge">{title}</Title>
        </TitleContainer>
        <TopButton {...topButton} />
      </TopContainer>
      {description}
    </FormSection>
    <ExpandLabelContainer onClick={onExpandClick}>
      <ExpandLabel>{t('expandLabel')}</ExpandLabel>
    </ExpandLabelContainer>
  </Section>
);

const CollapseFormButton = ({ t, action, show }) =>
  show ? (
    <CollapseFormContainer onClick={action}>
      <CollapseFormLabel size="small">{t('collapseLabel')}</CollapseFormLabel>
    </CollapseFormContainer>
  ) : null;

const TopButton = ({ icon, action = null }) =>
  icon ? (
    <TopButtonContainer onClick={action}>{icon}</TopButtonContainer>
  ) : null;

const FormTitle = ({ children }) =>
  children ? (
    <TitleContainer>
      <Title size="xlarge">{children}</Title>
    </TitleContainer>
  ) : null;

const handleClick = process => e => {
  if (e.button === 0) process();
  else {
    e.preventDefault();
  }
};

const FormButtons = ({
  handleGoBack,
  showOnlyButtons,
  isSubmitting,
  previousButton,
  nextButton,
  show,
  handleSubmit,
  isMultiForm,
  multiFormSubmit
}) => {
  const formType = isMultiForm
    ? {
        onMouseDown: handleClick(multiFormSubmit)
      }
    : showOnlyButtons
    ? { onMouseDown: handleClick(nextButton.action) }
    : {
        onMouseDown: handleClick(multiFormSubmit)
      };
  return show ? (
    <ButtonsContainer>
      {previousButton && (
        <Button
          id="previousButton"
          name="previousButton"
          key="previousButton"
          onMouseDown={handleClick(handleGoBack)}
          floated="left"
          chevron="left"
          tabIndex={previousButton.tabIndex || 0}
        >
          {previousButton.label}
        </Button>
      )}
      {nextButton && (
        <Button
          id="nextButton"
          {...formType}
          key="nextButton"
          disabled={isSubmitting}
          floated="right"
          chevron="right"
          colored="seablue"
          type="button"
          tabIndex={nextButton.tabIndex || 0}
        >
          {nextButton.label}
        </Button>
      )}
    </ButtonsContainer>
  ) : null;
};

const MandatoryFields = ({ t, show }) =>
  show ? (
    <MandatoryFieldsLabelContainer>
      <MandatoryFieldsLabel type="small">
        {t('mandatoryFields')}
      </MandatoryFieldsLabel>
    </MandatoryFieldsLabelContainer>
  ) : null;

const ErrorMessages = ({ messages }) =>
  messages.length ? (
    <MessageContainer>
      <Message>{messages}</Message>
    </MessageContainer>
  ) : null;

Form.propTypes = {
  onChange: PropTypes.func,
  formSchema: PropTypes.arrayOf(Array),
  useSection: PropTypes.bool,
  validationSchema: PropTypes.instanceOf(yup.object),
  title: PropTypes.string,
  previousButton: PropTypes.shape({
    label: PropTypes.string.isRequired,
    action: PropTypes.func.isRequired
  }),
  nextButton: PropTypes.shape({
    label: PropTypes.string.isRequired,
    action: PropTypes.func.isRequired
  }),
  topButton: PropTypes.shape({
    icon: PropTypes.object,
    action: PropTypes.func.isRequired
  }),
  showFormButtons: PropTypes.bool,
  showMandatoryFieldsLabel: PropTypes.bool,
  showMessages: PropTypes.bool,
  collapsable: PropTypes.bool,
  description: PropTypes.string,
  bottomComponent: PropTypes.object,
  showOnlyButtons: PropTypes.bool,
  fluid: PropTypes.bool,
  updateInitialValues: PropTypes.bool
};

Form.defaultProps = {
  collapsable: false,
  showOnlyButtons: false,
  showMandatoryFieldsLabel: true,
  showMessages: true,
  useSection: true,
  fluid: false,
  updateInitialValues: false
};

export default Form;
